import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidatorErrorEnum, PrimerStatusEnum, PrimerTypeEnum } from '@shared/enums';
import { RegexTypeEnum } from '@shared/enums/regex.enum';
import { CustomValidators } from '@shared/helpers';
import { PrimerSelectCategoryLabelEnum } from '../enums';
import { PrimerSelectNodeTypeEnum } from '../enums/primer-select-node-type.enum';
import {
    CustomPrimer,
    CustomPrimerFormGroup,
    NumberOfSamplesFormGroup,
    PrimerManagementData,
    PrimerManagementFormGroup,
    PrimerNode,
    ReactionDetailsForm,
    ReactionDetailsReactionForm,
    RecentPrimer,
    ShippedPrimerFormGroup,
    StandardPrimer,
    ValidateNumberOfSamples,
    ValidateReactionDetails,
} from '../interfaces';

export function computeNewCustomPrimer(
    primerName: string = '',
    sequence: string = ''
): FormGroup<CustomPrimerFormGroup> {
    return new FormGroup<CustomPrimerFormGroup>({
        name: new FormControl<string>(primerName, [
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(50),
            CustomValidators.emptyInputValidator,
        ]),
        sequence: new FormControl<string>(sequence, [
            Validators.required,
            Validators.minLength(15),
            Validators.maxLength(25),
            Validators.pattern(RegexTypeEnum.ORDERS_SEQUENCE_PATTERN),
        ]),
    });
}

export function computeNewShippedPrimer(
    primerName: string = '',
    barcode: string = ''
): FormGroup<ShippedPrimerFormGroup> {
    return new FormGroup<ShippedPrimerFormGroup>({
        name: new FormControl<string>(primerName, [
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(50),
            CustomValidators.emptyInputValidator,
        ]),
        barcode: new FormControl<string>(barcode, [Validators.required, CustomValidators.emptyInputValidator]),
    });
}

function createStandardPrimerNode(standardPrimers: StandardPrimer[]): PrimerNode {
    return {
        key: PrimerSelectCategoryLabelEnum.STANDARD_PRIMERS,
        type: PrimerSelectNodeTypeEnum.PARENT_NODE,
        selectable: false,
        children: standardPrimers.map(standardPrimer => {
            return {
                key: standardPrimer.name,
                type: PrimerSelectNodeTypeEnum.CHILD_NODE,
                data: { type: PrimerTypeEnum.STANDARD, isAlreadyUsed: false },
                selectable: true,
            };
        }),
    };
}

function createRecentPrimerNode(recentPrimers: RecentPrimer[]): PrimerNode {
    return {
        key: PrimerSelectCategoryLabelEnum.RECENT_PRIMERS,
        selectable: false,
        type: PrimerSelectNodeTypeEnum.PARENT_NODE,
        expanded: true,
        children: recentPrimers.map(recentPrimer => {
            return {
                key: recentPrimer.name,
                type: PrimerSelectNodeTypeEnum.CHILD_NODE,
                data: {
                    type: recentPrimer.sequence ? PrimerTypeEnum.RECENT_CUSTOM : PrimerTypeEnum.SHIPPED,
                    isAlreadyUsed: false,
                },
                selectable: true,
            };
        }),
    };
}

function createCustomPrimerNode(customPrimers: CustomPrimer[]): PrimerNode {
    return {
        key: PrimerSelectCategoryLabelEnum.CUSTOM_PRIMERS,
        selectable: false,
        type: PrimerSelectNodeTypeEnum.PARENT_NODE,
        children: customPrimers.map(customPrimer => {
            return {
                key: customPrimer.name,
                type: PrimerSelectNodeTypeEnum.CHILD_NODE,
                data: {
                    status: customPrimer.status,
                    type: customPrimer.status !== null ? PrimerTypeEnum.CUSTOM : PrimerTypeEnum.RECENT_CUSTOM,
                    isAlreadyUsed: false,
                },
                selectable: customPrimer.status !== PrimerStatusEnum.OUT_OF_STOCK,
            };
        }),
    };
}

export function mapPrimersToTreeSelectNodes(
    standardPrimers: StandardPrimer[],
    recentPrimers: RecentPrimer[],
    customPrimers: CustomPrimer[]
): PrimerNode[] {
    const primerNodes: PrimerNode[] = [];

    if (standardPrimers.length) {
        const standardPrimer: PrimerNode = createStandardPrimerNode(standardPrimers);
        primerNodes.push(standardPrimer);
    }

    if (recentPrimers.length) {
        const recentPrimersNode: PrimerNode = createRecentPrimerNode(recentPrimers);
        primerNodes.push(recentPrimersNode);
    }

    if (customPrimers.length) {
        const customPrimersNode: PrimerNode = createCustomPrimerNode(customPrimers);
        primerNodes.push(customPrimersNode);
    }

    return primerNodes;
}

export function areReactionDetailsBackendValid(
    response: ValidateReactionDetails,
    formGroup: FormGroup<ReactionDetailsForm>
) {
    if (
        response.expiredSamples.length === 0 &&
        response.invalidBarcodes.length === 0 &&
        response.usedBarcodes.length === 0 &&
        response.unreceivedSamples.length === 0 &&
        response.disposedSamples.length === 0 &&
        response.existingReactions.length === 0 &&
        response.emptySamples.length === 0 &&
        !response.areOutOfStockPrimersSelected
    ) {
        return true;
    }

    response.invalidBarcodes.forEach(invalidBarcode => {
        const control = formGroup.controls.reactions.controls.find(reaction => {
            return reaction.value.barcode?.trim() === invalidBarcode;
        });

        if (control) {
            control.controls.barcode.setErrors({ 'invalid-barcode': true });
        }
    });

    response.usedBarcodes.forEach(usedBarcode => {
        const control = formGroup.controls.reactions.controls.find(reaction => {
            return reaction.value.barcode?.trim() === usedBarcode;
        });

        if (control) {
            control.controls.barcode.setErrors({ 'used-barcode': true });
        }
    });

    response.expiredSamples.forEach(expiredSampleBarcode => {
        const control = formGroup.controls.reactions.controls.find(reaction => {
            return reaction.value.barcode?.trim() === expiredSampleBarcode;
        });

        if (control) {
            control.controls.barcode.setErrors({ 'expired-sample': true });
        }
    });

    response.existingReactions.forEach(existingReaction => {
        const control = formGroup.controls.reactions.controls.find(reaction => {
            return (
                reaction.controls.barcode.value?.trim() === existingReaction.sampleBarcode &&
                hasReactionInvalidPrimersSelected(reaction, existingReaction.primers)
            );
        });

        if (control) {
            control.controls.primerOption.setErrors({ [CustomValidatorErrorEnum.INVALID_PRIMER_SELECTED]: true });
        }
    });

    response.unreceivedSamples.forEach(unreceivedSample => {
        const control = formGroup.controls.reactions.controls.find(reaction => {
            return reaction.value.barcode?.trim() === unreceivedSample;
        });

        if (control) {
            control.controls.barcode.setErrors({ 'sample-not-received': true });
        }
    });

    response.disposedSamples.forEach(disposedSample => {
        const control = formGroup.controls.reactions.controls.find(reaction => {
            return reaction.value.barcode?.trim() === disposedSample;
        });

        if (control) {
            control.controls.barcode.setErrors({ 'disposed-sample': true });
        }
    });

    response.emptySamples.forEach(emptySample => {
        const control = formGroup.controls.reactions.controls.find(reaction => {
            return reaction.value.barcode?.trim() === emptySample;
        });

        if (control) {
            control.controls.barcode.setErrors({ 'empty-sample': true });
        }
    });

    return false;
}

function hasReactionInvalidPrimersSelected(
    reaction: FormGroup<ReactionDetailsReactionForm>,
    existingPrimers: string[]
): boolean {
    if (!reaction.controls.primerOption.value?.length || !existingPrimers.length) return false;

    const isAtLeaseOneInvalidPrimerSelected = reaction.controls.primerOption.value.some(selectedPrimer => {
        return existingPrimers.find(existingPrimer => existingPrimer === selectedPrimer.value);
    });

    return isAtLeaseOneInvalidPrimerSelected;
}

export function areNumberOfReactionsBackendValid(
    response: ValidateNumberOfSamples,
    formGroup: PrimerManagementFormGroup | NumberOfSamplesFormGroup
) {
    if (
        response.barcodeErrors.length === 0 &&
        response.nameErrors.length === 0 &&
        response.sequenceErrors.length === 0 &&
        response.usedBarcodes.length === 0
    ) {
        return true;
    }

    response.barcodeErrors.forEach(barcodeError => {
        const control = formGroup.shippedPrimers.controls.find(shippedPrimer => {
            return shippedPrimer.value.barcode?.trim() === barcodeError;
        });

        if (control) {
            control.controls.barcode.setErrors({ 'backend-invalid': true });
        }
    });

    response.sequenceErrors.forEach(sequenceError => {
        const control = formGroup.customPrimers.controls.find(customPrimer => {
            return customPrimer.value.sequence?.trim() === sequenceError;
        });

        if (control) {
            control.controls.sequence.setErrors({ 'not-unique': true });
        }
    });

    response.nameErrors.forEach(nameError => {
        const customPrimerControl: FormGroup<any> | undefined = formGroup.customPrimers.controls.find(customPrimer => {
            return customPrimer.value.name?.trim() === nameError;
        });

        const shippedPrimerControl = formGroup.shippedPrimers.controls.find(shippedPrimer => {
            return shippedPrimer.value.name?.trim() === nameError;
        });

        if (customPrimerControl) customPrimerControl.controls['name'].setErrors({ 'not-unique': true });
        if (shippedPrimerControl) shippedPrimerControl.controls['name'].setErrors({ 'not-unique': true });
    });

    response.usedBarcodes.forEach(usedBarcode => {
        const control = formGroup.shippedPrimers.controls.find(shippedPrimer => {
            return shippedPrimer.value.barcode?.trim() === usedBarcode;
        });

        if (control) {
            control.controls.barcode.setErrors({ 'not-unique': true });
        }
    });

    return false;
}

export function createStringUnicityDictionary(
    formArray: FormArray<FormGroup<any>>,
    controlName: string
): { [value: string]: number[] } {
    const indicesObject: { [value: string]: number[] } = {};
    for (let index = 0; index < formArray.controls.length; index++) {
        const controlValue = formArray.controls[index].controls[controlName]?.value?.trim();
        if (controlValue) {
            if (!indicesObject[controlValue]) {
                indicesObject[controlValue] = [];
            }
            indicesObject[controlValue].push(index);
        }
    }

    return indicesObject;
}

export function manageFrontEndNotUniqueError(
    indicesObject: { [value: string]: number[] },
    formArray: FormArray<FormGroup<any>>,
    controlName: string
) {
    Object.values(indicesObject).forEach(sequenceIndicesArray => {
        if (sequenceIndicesArray.length > 1) {
            sequenceIndicesArray.forEach(sequenceIndex => {
                formArray.controls[sequenceIndex].controls[controlName].setErrors({
                    [CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE]: true,
                    ...formArray.controls[sequenceIndex].controls[controlName].errors,
                });
            });
        } else {
            const sequenceControl = formArray.controls[sequenceIndicesArray[0]].controls[controlName];

            if (sequenceControl.hasError(CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE) && sequenceControl.errors) {
                delete sequenceControl.errors[CustomValidatorErrorEnum.FRONTEND_NOT_UNIQUE];
                sequenceControl.updateValueAndValidity();
            }
        }
    });
}

export function checkPrimersFrontendValidity(
    shippedPrimers: FormArray<FormGroup<ShippedPrimerFormGroup>>,
    customPrimers: FormArray<FormGroup<CustomPrimerFormGroup>>
): {
    invalidPrimersExist: boolean;
    primersExist: boolean;
} {
    let invalidPrimersExist = false;
    let primersExist = false;

    shippedPrimers.controls.forEach(shippedPrimerForm => {
        if (shippedPrimerForm.controls.barcode.value || shippedPrimerForm.controls.name.value) {
            shippedPrimerForm.markAllAsTouched();
            primersExist = true;
            if (shippedPrimerForm.invalid) invalidPrimersExist = true;
        }
    });

    customPrimers.controls.forEach(customPrimerForm => {
        if (customPrimerForm.controls.sequence.value || customPrimerForm.controls.name.value) {
            customPrimerForm.markAllAsTouched();
            primersExist = true;
            if (customPrimerForm.invalid) invalidPrimersExist = true;
        }
    });

    return {
        invalidPrimersExist: invalidPrimersExist,
        primersExist: primersExist,
    };
}

export function extractDataFromPrimerManagementForm(
    shippedPrimers: FormArray<FormGroup<ShippedPrimerFormGroup>>,
    customPrimers: FormArray<FormGroup<CustomPrimerFormGroup>>
): PrimerManagementData {
    return {
        customPrimers: customPrimers.value
            .filter(customPrimer => customPrimer.name && customPrimer.sequence)
            .map(customPrimer => {
                return { name: customPrimer.name ?? '', sequence: customPrimer.sequence ?? '' };
            }),
        shippedPrimers: shippedPrimers.value
            .filter(customPrimer => customPrimer.name && customPrimer.barcode)
            .map(shippedPrimer => {
                return { barcode: shippedPrimer.barcode ?? '', name: shippedPrimer.name ?? '' };
            }),
    };
}

export function createAffectedReactionForRemovePrimerConfirm(
    translateService: TranslateService,
    index: number
): string {
    return '\xa0\xa0\xa0\xa0\xa0' + '\u2022 ' + translateService.instant('reaction') + ' #' + (index + 1) + '\n\n';
}
